import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import {
  addMonths,
  endOfMonth,
  endOfWeek,
  format as formatDate,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns"
// date-fns v4's package.json "exports" map isn't understood by this
// project's tsconfig (`moduleResolution: "node"`, the pre-Node16/bundler
// classic resolver) for these two specifically -- every other date-fns
// export above resolves fine from the bare package barrel, but these need
// their own deep import path to be found under classic resolution.
import { eachDayOfInterval } from "date-fns/eachDayOfInterval"
import { parseISO } from "date-fns/parseISO"
import { cn } from "../../lib/utils"
import { colors } from "../../design-system/colors"
import { useCompanyCountry } from "../../hooks/useCompanyCountry"
import {
  getDateFormatForCountry,
  isoToDisplay,
  displayToIso,
  maskDigitsAsDisplay,
  isValidCalendarDate,
} from "../../lib/date-format"

export interface DateInputProps {
  id?: string;
  /** Always ISO (yyyy-mm-dd) or '' -- the same value Supabase/native date
   *  inputs use. This component only changes how a date is typed/shown,
   *  never how it's stored. */
  value: string;
  onChange: (isoValue: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const CALENDAR_HEIGHT_ESTIMATE = 300;

/**
 * The ONE date field every form should render through (Start Date, End
 * Date, or any other date field in the app). Two ways to set a date,
 * both converging on the same ISO `value`/`onChange`:
 *  - Typing raw digits ("18122002") live-formats into the company's
 *    regional order ("18/12/2002" for Colombia, "12/18/2002" for the US
 *    -- see lib/date-format.ts, driven by hooks/useCompanyCountry.ts).
 *  - A small calendar dropdown, opened on focus/click, for mouse pick.
 *
 * This renders its OWN calendar (not the native `<input type="date">`
 * picker) specifically so positioning is ours to control: a native
 * picker is an OS/browser-level overlay that anchors wherever it wants
 * and can cover the field -- see the "calendario tapa el input" bug this
 * replaced. The dropdown below is the same `absolute ... top-full mt-1`
 * pattern already used by ClientLeadCombobox's own list, with a flip to
 * `bottom-full` only when there's genuinely no room below.
 */
export const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  ({ id, value, onChange, placeholder, disabled, className }, ref) => {
    const country = useCompanyCountry();
    const format = getDateFormatForCountry(country);
    const [draft, setDraft] = React.useState(() => isoToDisplay(value, format));
    const [isOpen, setIsOpen] = React.useState(false);
    const [openUpward, setOpenUpward] = React.useState(false);
    const [viewMonth, setViewMonth] = React.useState(() => (value ? parseISO(value) : new Date()));
    const containerRef = React.useRef<HTMLDivElement>(null);

    // Keep the typed display in sync whenever the ISO value changes from
    // outside this input -- loading an existing record, a calendar pick
    // (below), or the company's format resolving after mount.
    React.useEffect(() => {
      setDraft(isoToDisplay(value, format));
    }, [value, format]);

    React.useEffect(() => {
      if (isOpen) setViewMonth(value ? parseISO(value) : new Date());
      // Only when the dropdown opens, not on every keystroke while it's open.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    React.useEffect(() => {
      if (!isOpen) return;
      const handleOutsideClick = (e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
          setIsOpen(false);
        }
      };
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setIsOpen(false);
      };
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('mousedown', handleOutsideClick);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [isOpen]);

    const openCalendar = () => {
      if (disabled) return;
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const roomBelow = window.innerHeight - rect.bottom;
        setOpenUpward(roomBelow < CALENDAR_HEIGHT_ESTIMATE && rect.top > CALENDAR_HEIGHT_ESTIMATE);
      }
      setIsOpen(true);
    };

    const commitDigits = (digits: string) => {
      if (digits.length !== 8) return;
      const iso = displayToIso(digits, format);
      if (iso && isValidCalendarDate(iso)) onChange(iso);
    };

    const weekDayLabels = React.useMemo(() => {
      const start = startOfWeek(new Date());
      return eachDayOfInterval({ start, end: endOfWeek(new Date()) }).map((d: Date) => formatDate(d, 'EEEEE'));
    }, []);

    const days = React.useMemo(() => {
      const start = startOfWeek(startOfMonth(viewMonth));
      const end = endOfWeek(endOfMonth(viewMonth));
      return eachDayOfInterval({ start, end });
    }, [viewMonth]);

    const selectedDate = value ? parseISO(value) : null;

    return (
      <div ref={containerRef} className="relative">
        <input
          id={id}
          ref={ref}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          disabled={disabled}
          value={draft}
          placeholder={placeholder || format}
          onFocus={(e) => {
            openCalendar();
            e.target.select();
          }}
          onClick={(e) => {
            openCalendar();
            e.currentTarget.select();
          }}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
            setDraft(maskDigitsAsDisplay(digits));
            if (digits.length === 0) {
              onChange('');
              return;
            }
            commitDigits(digits);
          }}
          className={cn(
            "flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-secondary-foreground placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        />
        {isOpen && !disabled && (
          <div
            className={cn(
              "absolute z-50 w-64 bg-white border border-gray-300 rounded-lg shadow-lg p-3",
              openUpward ? "bottom-full mb-1" : "top-full mt-1"
            )}
          >
            <div className="flex items-center justify-between mb-2">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setViewMonth((m: Date) => subMonths(m, 1))}
                className="p-1 rounded hover:bg-gray-100"
              >
                <ChevronLeft className="h-4 w-4 text-gray-600" />
              </button>
              <span className="text-sm font-medium text-gray-900">{formatDate(viewMonth, 'MMMM yyyy')}</span>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setViewMonth((m: Date) => addMonths(m, 1))}
                className="p-1 rounded hover:bg-gray-100"
              >
                <ChevronRight className="h-4 w-4 text-gray-600" />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-1">
              {weekDayLabels.map((label: string, i: number) => (
                <div key={i} className="text-center text-xs text-gray-500">{label}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {days.map((day: Date) => {
                const inMonth = isSameMonth(day, viewMonth);
                const selected = !!selectedDate && isSameDay(day, selectedDate);
                const today = isSameDay(day, new Date());
                return (
                  <button
                    key={day.toISOString()}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onChange(formatDate(day, 'yyyy-MM-dd'));
                      setIsOpen(false);
                    }}
                    // the selected day is KrewUp's blue (colors.buttonSecondary, the 'blue' button's colour), white text
                    style={selected ? { backgroundColor: colors.buttonSecondary } : undefined}
                    aria-pressed={selected}
                    className={cn(
                      "h-7 w-7 text-xs rounded-md flex items-center justify-center",
                      !inMonth && "text-gray-300",
                      inMonth && !selected && "text-gray-700 hover:bg-gray-100",
                      selected && "text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-ring",
                      today && !selected && "border border-gray-400"
                    )}
                  >
                    {formatDate(day, 'd')}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    )
  }
)
DateInput.displayName = "DateInput"
